import { DRONECAST_PROTOCOL, IDCMessage, TDCCastroomId, TDCPayloadCastStream, TDCPayloadExchangeIcecandidate, TDCPayloadJoinCastroom, TDCPayloadSubscribeStream, TDCMessageType, MediaServerType, VideoSourceType } from './clientInterfaces'

/**
 * `dronecast/1.0` 정식 이름 ↔ 레거시 이름.
 *
 * 사양(`code/shared/protocol/01_messages.md` §3)은 `res.*`·`evt.*` 를 정본으로 정했는데
 * 서버 **송신**은 오랫동안 레거시 이름을 썼다(수신은 두 이름을 다 받았다). 서버를
 * 정본으로 옮기는 동안 클라이언트가 양쪽을 다 받아야 한 쪽만 먼저 바꿔도 안 깨진다.
 *
 * 핸들러는 어느 이름으로 등록해도 된다 — 디스패치에서 짝을 찾아준다.
 * 서버 전환이 끝나면 이 표와 레거시 등록을 함께 지운다.
 */
const MESSAGE_ALIAS: Record<string, string> = {
  'res.castroom.create': 'create_castroom_result',
  'create_castroom_result': 'res.castroom.create',
  'res.casting.start': 'cast_stream_result',
  'cast_stream_result': 'res.casting.start',
  'evt.castroom.status_changed': 'castroom_status_changed',
  'castroom_status_changed': 'evt.castroom.status_changed',
}

export class DronecastClient {
  private ws: WebSocket | null = null
  private messageHandlers: Map<string, (message: IDCMessage) => void> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 3000
  private isConnecting = false
  private pendingMessages: IDCMessage[] = []
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  private type: 'caster' | 'viewer' = 'caster'
  private castroomId?: TDCCastroomId
  private rtspUrl?: string
  private connectionUrl = ''

  onWsStateChange?: (state: 'connecting' | 'connected' | 'reconnecting' | 'disconnected') => void

  constructor(type: 'caster' | 'viewer') { this.type = type }

  connect(url: string) {
    if (this.isConnecting) return
    // 기존 WS가 있으면 정리 (고아 WS 방지)
    if (this.ws) {
      console.log('[DronecastClient] connect: 기존 WS 정리')
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.close()
      this.ws = null
    }
    this.cancelReconnect()
    this.isConnecting = true
    this.connectionUrl = url
    this.onWsStateChange?.('connecting')
    this.ws = new WebSocket(this.connectionUrl)

    this.ws.onopen = () => {
      this.isConnecting = false
      this.reconnectAttempts = 0
      this.cancelReconnect()
      this.onWsStateChange?.('connected')
      this.sendPendingMessages()
    }
    this.ws.onmessage = async (event) => {
      try {
        const message: IDCMessage = JSON.parse(event.data)
        console.log(`[DronecastClient] S->C: ${message.type}`, message.payload)
        const handler = this.messageHandlers.get(message.type)
          ?? this.messageHandlers.get(MESSAGE_ALIAS[message.type])
        if (handler) {
          await handler(message)
        } else {
          console.warn(`[DronecastClient] 핸들러 없음: ${message.type}`)
        }
      } catch (err) {
        console.error('[DronecastClient] 메시지 처리 오류:', err, event.data)
      }
    }
    this.ws.onclose = () => {
      this.isConnecting = false
      this.onWsStateChange?.('disconnected')
      this.reconnect()
    }
    this.ws.onerror = () => {
      this.isConnecting = false
      this.onWsStateChange?.('disconnected')
    }
  }

  private reconnect() {
    // 이미 연결되어 있거나 연결 중이면 재연결 불필요
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) return
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return
    if (this.reconnectTimer) return  // 이미 예약된 재연결이 있으면 중복 방지
    this.reconnectAttempts++
    this.onWsStateChange?.('reconnecting')
    console.log(`[DronecastClient] 재연결 예약 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect(this.connectionUrl)
    }, this.reconnectDelay)
  }

  private cancelReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private sendPendingMessages() {
    while (this.pendingMessages.length > 0) {
      const msg = this.pendingMessages.shift()
      if (msg) this.send(msg)
    }
  }

  createCastroom(castroomId: TDCCastroomId, options?: { rtspUrl?: string; mediaServerType?: MediaServerType; sourceType?: VideoSourceType }) {
    console.log('[DronecastClient] createCastroom input:', castroomId, typeof castroomId)
    console.log('[DronecastClient] gcs:', castroomId?.gcs, 'drone:', castroomId?.drone, 'camera:', castroomId?.camera)
    const plainId = { gcs: castroomId.gcs, drone: castroomId.drone, camera: castroomId.camera }
    console.log('[DronecastClient] plainId:', JSON.stringify(plainId))
    this.castroomId = plainId; this.rtspUrl = options?.rtspUrl
    const message: IDCMessage = {
      protocol: DRONECAST_PROTOCOL,
      type: 'create_castroom',
      payload: {
        castroomId: plainId,
        rtspUrl: this.rtspUrl,
        ...(options?.mediaServerType && { mediaServerType: options.mediaServerType }),
        ...(options?.sourceType && { sourceType: options.sourceType }),
      }
    }
    this.send(message)
  }

  exchangeIceCandidate(candidate: RTCIceCandidate) {
    if (!candidate || !this.castroomId) return
    const id = this.castroomId
    const message: IDCMessage = {
      protocol: DRONECAST_PROTOCOL,
      type: 'exchange_icecandidate',
      payload: { castroomId: { gcs: id.gcs, drone: id.drone, camera: id.camera }, type: this.type, candidate } as TDCPayloadExchangeIcecandidate
    }
    this.send(message)
  }

  castStream(sdpOffer: string) {
    if (!this.castroomId) return
    const message: IDCMessage = {
      protocol: DRONECAST_PROTOCOL,
      type: 'cast_stream',
      payload: { castroomId: this.castroomId, sdpOffer } as TDCPayloadCastStream
    }
    this.send(message)
  }

  joinCastroom(castroomId: TDCCastroomId, retryCount?: number) {
    const plainId = { gcs: castroomId.gcs, drone: castroomId.drone, camera: castroomId.camera }
    this.castroomId = plainId
    const message: IDCMessage = {
      protocol: DRONECAST_PROTOCOL,
      type: 'join_castroom',
      payload: { castroomId: plainId, retryCount: retryCount || 0 } as TDCPayloadJoinCastroom
    }
    this.send(message)
  }

  closeCastroom() {
    if (!this.castroomId) return
    const id = this.castroomId
    const message: IDCMessage = {
      protocol: DRONECAST_PROTOCOL,
      type: 'close_castroom',
      payload: { castroomId: { gcs: id.gcs, drone: id.drone, camera: id.camera } }
    }
    this.send(message)
  }

  subscribeStream(sdpOffer: string) {
    if (!this.castroomId) return
    const message: IDCMessage = {
      protocol: DRONECAST_PROTOCOL,
      type: 'subscribe_stream',
      payload: { castroomId: this.castroomId, sdpOffer } as TDCPayloadSubscribeStream
    }
    this.send(message)
  }

  send(message: IDCMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`[DronecastClient] WS 미연결 (readyState=${this.ws?.readyState}), 큐잉: ${message.type}`)
      this.pendingMessages.push(message)
      // CONNECTING(0) 상태면 곧 연결되므로 재연결 불필요, CLOSED/CLOSING일 때만 재연결
      if (!this.ws || this.ws.readyState > WebSocket.OPEN) {
        this.reconnect()
      }
      return
    }
    console.log(`[DronecastClient] C->S: ${message.type}`, message.payload)
    try { this.ws.send(JSON.stringify(message)) } catch (err) {
      console.error(`[DronecastClient] 전송 실패: ${message.type}`, err)
      this.pendingMessages.push(message)
    }
  }

  setMessageHandler(type: TDCMessageType, handler: (message: IDCMessage) => void) { this.messageHandlers.set(type, handler) }
  removeHandler(type: string) { this.messageHandlers.delete(type) }
  close() {
    this.cancelReconnect()
    if (this.ws) {
      this.ws.onclose = null  // 의도적 종료 시 자동 재연결 방지
      this.ws.close()
      this.ws = null
    }
  }
}


